/* tslint:disable:no-console */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import {Observable, Subject, Subscriber} from 'rxjs';
import {getLocale} from '../i18n/helper';
import {IResponse20x, IRxAxiosConfig, IRxAxiosError} from './types';

export class RxAxios {
  private errors: Subject<IRxAxiosError>;
  private success: Subject<IResponse20x>;
  private httpClient: AxiosInstance;
  private accessToken: string | null = null;
  private locale: string;
  private xIdSession: string | null = null;

  constructor(options: IRxAxiosConfig = {}) {
    this.httpClient = axios.create(options);
    this.errors = new Subject<IRxAxiosError>();
    this.success = new Subject<IResponse20x>();
    this.locale = getLocale();
  }

  public setToken(token: string | null): void {
    this.accessToken = token;
  }

  public setXSession(xIdSession: string | null): void {
    this.xIdSession = xIdSession;
  }

  public get<T = any>(url: string, queryParams?: object) {
    return this._makeRequest<T>('GET', url, {params: queryParams});
  }

  public post<T = any>(url: string, body?: object, queryParams?: object) {
    return this._makeRequest<T>('POST', url, {params: queryParams}, body);
  }

  public put<T = any>(url: string, body: object, queryParams?: object) {
    return this._makeRequest<T>('PUT', url, {params: queryParams}, body);
  }

  public patch<T = any>(url: string, body: object, queryParams?: object) {
    return this._makeRequest<T>('PATCH', url, {params: queryParams}, body);
  }

  public delete<T = any>(url: string, queryParams?: object) {
    return this._makeRequest('DELETE', url, {params: queryParams});
  }

  public request<T = any>(config: AxiosRequestConfig): Observable<T> {
    const request = this.httpClient.request(config);
    return this._toObservable(request);
  }

  public listenError(): Observable<IRxAxiosError> {
    return this.errors.asObservable();
  }

  public listenSuccess(): Observable<IResponse20x> {
    return this.success.asObservable();
  }

  private _makeRequest<T>(
    method: string,
    url: string,
    config?: AxiosRequestConfig,
    body?: object
  ) {
    const request = this._builderHttp<T>(
      method,
      url,
      this._customHeader(config),
      body
    );
    return this._toObservable(request);
  }

  private _customHeader(config?: AxiosRequestConfig): AxiosRequestConfig {
    // TODO desacloplar isso de dentro da lib, criar um method ser headers aqui na class
    // entao aqui deve apenas fazer merde destes headers
    let headers: any = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    if (this.xIdSession) {
      headers['Election-Host'] = this.xIdSession;
    }

    if (this.locale) {
      headers['x-Accept-Language'] = this.locale;
    }

    if (config) {
      headers = Object.assign({}, headers, config.headers);
    }

    return {
      ...config,
      headers,
    };
  }

  private _toObservable<T>(request: AxiosPromise<T>): Observable<T> {
    return new Observable<T>((subscriber: Subscriber<T>) => {
      request
        .then((response: AxiosResponse) => {
          this.success.next(response.data);
          subscriber.next(response.data);
          subscriber.complete();
        })
        .catch((err: AxiosError) => {
          const responseError = this._presentError(err);
          this.errors.next(responseError);
          subscriber.error(responseError);
          subscriber.complete();
        });
    });
  }

  private _presentError(err: AxiosError) {
    const dataError: IRxAxiosError = {
      error_type: 'exception',
      isAxiosError: false,
      message: 'Error',
    };

    if (!err.isAxiosError) {
      dataError.isAxiosError = false;
      dataError.error_type = 'exception';
    } else {
      dataError.isAxiosError = true;
      dataError.axiosError = err;

      // Error Axio 😨
      if (err.response) {
        /*
         * The request was made and the server responded with a
         * status code that falls out of the range of 2xx
         */
        console.log('Axios Error response', err.response);
        dataError.response = err.response;
        dataError.error_type = 'response';

        if (err.response.data.error && err.response.data.error.http_code) {
          dataError.apiErrorResponse = err.response.data;
          dataError.message = err.response.data.message;
        }
      } else if (err.request) {
        /*
         * The request was made but no response was received, `error.request`
         * is an instance of XMLHttpRequest in the browser and an instance
         * of http.ClientRequest in Node.js
         */
        console.log('Axios Error request', err.request);
        dataError.request = err.request;
        dataError.error_type = 'request';
      }
    }

    console.log(dataError);
    return dataError;
  }

  private _builderHttp<T>(
    method: string,
    url: string,
    config?: AxiosRequestConfig,
    body?: object
  ): AxiosPromise<T> {
    switch (method) {
      case 'GET':
        return this.httpClient.get<T>(url, config);

      case 'POST':
        return this.httpClient.post<T>(url, body, config);

      case 'PUT':
        return this.httpClient.put<T>(url, body, config);

      case 'PATCH':
        return this.httpClient.patch<T>(url, body, config);

      case 'DELETE':
        return this.httpClient.delete(url, config);

      default:
        throw new Error('Method not supported');
    }
  }
}



// WEBPACK FOOTER //
// ./src/shared/rx-axios.ts